import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { firestore } from '../firebase/config';
import { setDoc, doc, getDoc, getDocs, collection, addDoc } from 'firebase/firestore';
import { auth, updatePassword } from '../firebase/config';
import { createUserWithEmailAndPassword, signInWithEmailAndPassword } from 'firebase/auth';
import { useToast } from '../components/ToastContext';
import { encryptPassword, isPasswordHashed, encryptForAdmin } from '../utils/passwordUtils';
import { generateInvoiceForPendingFee } from '../utils/invoiceUtils';

function AddStudent() {
  const [form, setForm] = useState({
    fullName: '',
    fatherName: '',
    address: '',
    cnicNo: '',
    contactNumber: '',
    secondaryContactNumber: '', // new field
    password: '',
    courseName: '',
    admissionDate: '',
    totalFee: '',
    submittedFee: '',
    pendingFee: '',
    pendingFeeSubmissionDate: ''
  });
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [buttonState, setButtonState] = useState('idle'); // 'idle', 'loading', 'success'
  const [courses, setCourses] = useState([]);
  const [coursesLoading, setCoursesLoading] = useState(true);
  const [errors, setErrors] = useState({});
  const [existingStudentData, setExistingStudentData] = useState(null);
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { showSuccess, showError } = useToast();

  useEffect(() => {
    if (id) {
      // Edit mode: fetch student data
      const fetchStudent = async () => {
        setLoading(true);
        try {
          const studentDoc = await getDoc(doc(firestore, 'students', id));
          if (studentDoc.exists()) {
            const data = studentDoc.data();
            setExistingStudentData(data); // Store existing data for later use
            setForm({
              fullName: data.fullName || '',
              fatherName: data.fatherName || '',
              address: data.address || '',
              cnicNo: data.cnicNo || '',
              contactNumber: data.contactNumber || '',
              secondaryContactNumber: data.secondaryContactNumber || '',
              password: data.password || '', // Load password in edit mode
              courseName: data.courseName || '',
              admissionDate: data.admissionDate || '',
              totalFee: data.totalFee || '',
              submittedFee: data.submittedFee || '',
              pendingFee: data.pendingFee || '',
              pendingFeeSubmissionDate: data.pendingFeeSubmissionDate || ''
            });
          }
        } catch (err) {
          showError('Error fetching student: ' + err.message);
        }
        setLoading(false);
      };
      fetchStudent();
    }
  }, [id]);

  useEffect(() => {
    const total = parseFloat(form.totalFee) || 0;
    const submitted = parseFloat(form.submittedFee) || 0;
    const pending = total - submitted;
    setForm(prev => ({
      ...prev,
      pendingFee: pending > 0 ? pending.toString() : '0'
    }));
  }, [form.totalFee, form.submittedFee]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
    // Clear error when field is filled
    setErrors(prev => ({ ...prev, [name]: undefined }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    // Custom validation
    const newErrors = {};
    if (!form.fullName.trim()) newErrors.fullName = 'Full Name is required.';
    if (!form.fatherName.trim()) newErrors.fatherName = "Father's Name is required.";
    if (!form.address.trim()) newErrors.address = 'Address is required.';
    if (!form.cnicNo.trim()) newErrors.cnicNo = 'CNIC Number is required.';
    if (!form.contactNumber.trim()) newErrors.contactNumber = 'Contact Number is required.'; // Required for both add and edit
    if (!form.password.trim()) newErrors.password = 'Password is required.'; // Required for both add and edit
    if (!form.courseName.trim()) newErrors.courseName = 'Course Name is required.';
    if (!form.admissionDate.trim()) newErrors.admissionDate = 'Admission Date is required.';
    if (!form.totalFee.trim()) newErrors.totalFee = 'Total Fee is required.';
    if (!form.submittedFee.trim()) newErrors.submittedFee = 'Submitted Fee is required.';
    // No required for secondaryContactNumber or pendingFeeSubmissionDate
    setErrors(newErrors);
    if (Object.keys(newErrors).length > 0) return;
    setLoading(true);
    setButtonState('loading');
    try {
      if (id) {
        // Edit mode: update student
        const updateData = {
          ...form,
          uid: id,
        };
        
        // Check for duplicate contact number (excluding current student)
        const studentsSnap = await getDocs(collection(firestore, 'students'));
        const employeesSnap = await getDocs(collection(firestore, 'employees'));
        const duplicateStudent = studentsSnap.docs.find(doc => 
          doc.data().contactNumber === form.contactNumber && doc.id !== id
        );
        const duplicateEmployee = employeesSnap.docs.find(doc => 
          doc.data().contactNumber === form.contactNumber
        );
        if (duplicateStudent || duplicateEmployee) {
          showError('This contact number is already registered with another user.');
          setLoading(false);
          setButtonState('idle');
          return;
        }
        
        // Update student document
        await setDoc(doc(firestore, 'students', id), {
          ...updateData,
          createdAt: existingStudentData?.createdAt || new Date().toISOString(), // Preserve existing createdAt or add new one
        });
        
        // Update user document for login credentials
        const authEmail = `${form.contactNumber}@student.com`;
        
        // Encrypt password if it's not already hashed
        let encryptedPassword = form.password;
        if (!isPasswordHashed(form.password)) {
          encryptedPassword = await encryptPassword(form.password);
        }
        
        // Create admin-visible password
        const adminPassword = encryptForAdmin(form.password);
        
        await setDoc(doc(firestore, 'users', id), {
          fullName: form.fullName,
          contactNumber: form.contactNumber,
          role: 'student',
          uid: id,
          authEmail,
          password: encryptedPassword,
          adminPassword: adminPassword, // For admin visibility
        }, { merge: true });
        
        // Check if pending fees have changed and generate invoice if needed
        const newPendingAmount = parseFloat(form.pendingFee) || 0;
        const oldPendingAmount = parseFloat(existingStudentData.pendingFee) || 0;
        
        console.log('Invoice generation check:', {
          studentName: form.fullName,
          oldPendingAmount,
          newPendingAmount,
          oldPendingFeeRaw: existingStudentData.pendingFee,
          newPendingFeeRaw: form.pendingFee,
          hasChanged: newPendingAmount !== oldPendingAmount,
          timestamp: new Date().toISOString()
        });
        
        if (newPendingAmount !== oldPendingAmount) {
          // Generate invoice for any change in pending fees (increase, decrease, or full payment)
          console.log('Generating invoice for fee change');
                     // For existing students, we need to use the UID that students actually use to authenticate
           // This is the same as the document ID in the students collection
           const studentData = {
             id: id, // Document ID
             uid: id, // UID that students use (same as document ID for existing students)
             fullName: form.fullName,
             totalFee: form.totalFee,
             submittedFee: form.submittedFee,
             pendingFee: form.pendingFee
           };
          console.log('Student data for invoice generation:', studentData);
          console.log('Student UID being used for invoice:', id);
          console.log('Student document ID (from URL params):', id);
          console.log('Student UID from existing data:', existingStudentData?.uid);
          const result = await generateInvoiceForPendingFee(studentData, newPendingAmount, showSuccess, showError);
          console.log('Invoice generation result:', result);
        } else {
          console.log('No fee change detected, skipping invoice generation');
        }
        
        setButtonState('success');
        setTimeout(() => {
          showSuccess('Student updated successfully');
          navigate('/admin?tab=students');
        }, 1000);
      } else {
        // Duplicate contact number check
        const studentsSnap = await getDocs(collection(firestore, 'students'));
        const employeesSnap = await getDocs(collection(firestore, 'employees'));
        const duplicateStudent = studentsSnap.docs.find(doc => doc.data().contactNumber === form.contactNumber);
        const duplicateEmployee = employeesSnap.docs.find(doc => doc.data().contactNumber === form.contactNumber);
        if (duplicateStudent || duplicateEmployee) {
          showError('This contact number is already registered with another user.');
          setLoading(false);
          return;
        }
        // Add mode: create new student
        const authEmail = `${form.contactNumber}@student.com`;
        
        // Generate a unique UID for the student
        const uid = `student_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
        
        // Encrypt password for storage
        const encryptedPassword = await encryptPassword(form.password);
        
        // Create admin-visible password
        const adminPassword = encryptForAdmin(form.password);
        
        // Create student document first
        await setDoc(doc(firestore, 'students', uid), {
          ...form,
          authEmail,
          uid,
          password: encryptedPassword, // Store encrypted password
          adminPassword: adminPassword, // For admin visibility
          createdAt: new Date().toISOString(), // Add creation timestamp for sorting
        });
        
        // Create user document for login
        await setDoc(doc(firestore, 'users', uid), {
          fullName: form.fullName,
          contactNumber: form.contactNumber,
          role: 'student', // Keep consistent lowercase
          uid,
          authEmail,
          password: encryptedPassword, // Store encrypted password
          adminPassword: adminPassword, // For admin visibility
        });
        
        // Generate invoice automatically if there are pending fees
        const pendingAmount = parseFloat(form.pendingFee) || 0;
        if (pendingAmount > 0) {
          const studentData = {
            id: uid,
            uid: uid, // Add uid field for consistency
            fullName: form.fullName,
            totalFee: form.totalFee,
            submittedFee: form.submittedFee,
            pendingFee: form.pendingFee
          };
          await generateInvoiceForPendingFee(studentData, pendingAmount, showSuccess, showError);
        } else if (pendingAmount === 0 && parseFloat(form.submittedFee) > 0) {
          // Generate invoice when all fees are paid
          const studentData = {
            id: uid,
            uid: uid, // Add uid field for consistency
            fullName: form.fullName,
            totalFee: form.totalFee,
            submittedFee: form.submittedFee,
            pendingFee: form.pendingFee
          };
          await generateInvoiceForPendingFee(studentData, 0, showSuccess, showError);
        }
        
        setButtonState('success');
        setTimeout(() => {
          showSuccess('Student added successfully');
          navigate('/admin?tab=students');
        }, 1000);
      }
    } catch (error) {
      showError('Error: ' + error.message);
      setButtonState('idle');
    }
    setLoading(false);
  };

  const fetchCourses = async () => {
    try {
      setCoursesLoading(true);
      const coursesRef = collection(firestore, 'courses');
      const snapshot = await getDocs(coursesRef);
      const courseList = snapshot.docs.map(doc => ({
        id: doc.id,
        name: doc.data().name
      }));
      setCourses(courseList);
    } catch (err) {
      showError('Error fetching courses: ' + err.message);
    } finally {
      setCoursesLoading(false);
    }
  };

  useEffect(() => {
    fetchCourses();
  }, []);

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-100 py-8">
      <div className="bg-white border border-gray-300 rounded-lg shadow max-w-2xl w-full p-8 print:p-4">
        {/* Back Button */}
        <div className="mb-6">
          <div className="uiverse-box">
          <button 
            type="button"
            onClick={() => {
              const params = new URLSearchParams(location.search);
              if (params.get('tab') === 'students') {
                navigate('/admin?tab=students');
              } else {
                navigate('/admin');
              }
            }}
            className="flex items-center px-0 py-0 bg-transparent text-black rounded-full font-bold text-base gap-2 focus:outline-none border-none shadow-none"
          >
            <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
            </svg>
            Back to Dashboard
          </button>
          </div>
        </div>
        {/* Header */}
        <div className="mb-6 text-center">
          <h1 className="text-2xl font-bold tracking-wide mb-1">DIGITAL SKILLS HOUSE</h1>
          <h2 className="text-lg font-semibold underline">Student Admission Form</h2>
        </div>
        <form onSubmit={handleSubmit}>
          {/* Error summary at the top */}
          {Object.keys(errors).length > 0 && (
            <div className="mb-6 p-3 bg-red-100 border border-red-300 rounded text-red-700 text-sm">
              Please fill all required fields.
            </div>
          )}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {/* Personal Info Section (Left) */}
            <div className="border border-blue-300 bg-blue-50 rounded p-6 shadow-sm flex flex-col h-full">
              <h3 className="font-bold text-blue-700 flex items-center mb-4 text-lg">
                <svg className="w-6 h-6 mr-2 text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" /></svg>
                Personal Information
              </h3>
              <div className="grid grid-cols-1 gap-6">
                <div>
                  <label className="block text-sm font-medium mb-1">Full Name *</label>
                  <input type="text" name="fullName" value={form.fullName} onChange={handleChange} className="w-full border-b border-gray-400 bg-transparent focus:outline-none py-1 px-2" />
                  {errors.fullName && <p className="text-red-600 text-xs mt-1 bg-red-50 px-2 py-1 rounded">{errors.fullName}</p>}
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Father's Name *</label>
                  <input type="text" name="fatherName" value={form.fatherName} onChange={handleChange} className="w-full border-b border-gray-400 bg-transparent focus:outline-none py-1 px-2" />
                  {errors.fatherName && <p className="text-red-600 text-xs mt-1 bg-red-50 px-2 py-1 rounded">{errors.fatherName}</p>}
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Address *</label>
                  <input type="text" name="address" value={form.address} onChange={handleChange} className="w-full border-b border-gray-400 bg-transparent focus:outline-none py-1 px-2" />
                  {errors.address && <p className="text-red-600 text-xs mt-1 bg-red-50 px-2 py-1 rounded">{errors.address}</p>}
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">CNIC Number *</label>
                  <input type="text" name="cnicNo" value={form.cnicNo} onChange={handleChange} placeholder="00000-0000000-0" className="w-full border-b border-gray-400 bg-transparent focus:outline-none py-1 px-2" />
                  {errors.cnicNo && <p className="text-red-600 text-xs mt-1 bg-red-50 px-2 py-1 rounded">{errors.cnicNo}</p>}
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Contact Number *</label>
                  <input type="tel" name="contactNumber" value={form.contactNumber} onChange={handleChange} placeholder="03XX-XXXXXXX" className="w-full border-b border-gray-400 bg-transparent focus:outline-none py-1 px-2" />
                  {errors.contactNumber && <p className="text-red-600 text-xs mt-1 bg-red-50 px-2 py-1 rounded">{errors.contactNumber}</p>}
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Secondary/Father Contact Number</label>
                  <input type="tel" name="secondaryContactNumber" value={form.secondaryContactNumber} onChange={handleChange} placeholder="03XX-XXXXXXX" className="w-full border-b border-gray-400 bg-transparent focus:outline-none py-1 px-2" />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Password *</label>
                  <input type="password" name="password" value={form.password} onChange={handleChange} placeholder={id ? "Update password for student login" : "Set a password for student login"} className="w-full border-b border-gray-400 bg-transparent focus:outline-none py-1 px-2" />
                  {errors.password && <p className="text-red-600 text-xs mt-1 bg-red-50 px-2 py-1 rounded">{errors.password}</p>}
                </div>
              </div>
            </div>
            {/* Course + Fee Info Section (Right) */}
            <div className="flex flex-col gap-8 h-full">
              <div className="border border-purple-300 bg-purple-50 rounded p-6 shadow-sm flex-1">
                <h3 className="font-bold text-purple-700 flex items-center mb-4 text-lg">
                  <svg className="w-6 h-6 mr-2 text-purple-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.746 0 3.332.477 4.5 1.253v13C19.832 18.477 18.246 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" /></svg>
                  Course Information
                </h3>
                <div className="grid grid-cols-1 gap-6">
                  <div>
                    <label className="block text-sm font-medium mb-1">Course Name *</label>
                    <select name="courseName" value={form.courseName} onChange={handleChange} className="w-full border-b border-gray-400 bg-transparent focus:outline-none py-1 px-2">
                      <option value="">
                        {coursesLoading ? 'Loading courses...' : courses.length === 0 ? 'No courses available' : 'Select a course'}
                      </option>
                      {courses.map(course => (
                        <option key={course.id} value={course.name}>{course.name}</option>
                      ))}
                    </select>
                    {courses.length === 0 && !coursesLoading && (
                      <p className="text-sm text-red-600 mt-1">
                        No courses available. Please add courses from the admin dashboard first.
                      </p>
                    )}
                    {errors.courseName && <p className="text-red-600 text-xs mt-1 bg-red-50 px-2 py-1 rounded">{errors.courseName}</p>}
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">Admission Date *</label>
                    <input type="date" name="admissionDate" value={form.admissionDate} onChange={handleChange} className="w-full border-b border-gray-400 bg-transparent focus:outline-none py-1 px-2" />
                    {errors.admissionDate && <p className="text-red-600 text-xs mt-1 bg-red-50 px-2 py-1 rounded">{errors.admissionDate}</p>}
                  </div>
                </div>
              </div>
              <div className="border border-green-300 bg-green-50 rounded p-6 shadow-sm flex-1">
                <h3 className="font-bold text-green-700 flex items-center mb-4 text-lg">
                  <svg className="w-6 h-6 mr-2 text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.746 0 3.332.477 4.5 1.253v13C19.832 18.477 18.246 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" /></svg>
                  Fee Information
                </h3>
                <div className="grid grid-cols-1 gap-6">
                  <div>
                    <label className="block text-sm font-medium mb-1">Total Fee (PKR) *</label>
                    <input type="number" name="totalFee" value={form.totalFee} onChange={handleChange} className="w-full border-b border-gray-400 bg-transparent focus:outline-none py-1 px-2" />
                    {errors.totalFee && <p className="text-red-600 text-xs mt-1 bg-red-50 px-2 py-1 rounded">{errors.totalFee}</p>}
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">Submitted Fee (PKR) *</label>
                    <input type="number" name="submittedFee" value={form.submittedFee} onChange={handleChange} className="w-full border-b border-gray-400 bg-transparent focus:outline-none py-1 px-2" />
                    {errors.submittedFee && <p className="text-red-600 text-xs mt-1 bg-red-50 px-2 py-1 rounded">{errors.submittedFee}</p>}
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">Pending Fee (PKR)</label>
                    <input type="number" name="pendingFee" value={form.pendingFee} readOnly className="w-full border-b border-gray-400 bg-gray-100 focus:outline-none py-1 px-2 font-semibold" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">Pending Fee Submission Date</label>
                    <input type="date" name="pendingFeeSubmissionDate" value={form.pendingFeeSubmissionDate} onChange={handleChange} className="w-full border-b border-gray-400 bg-transparent focus:outline-none py-1 px-2" />
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className="flex justify-center mt-8">
            <button 
              type="submit" 
              className={`
                px-8 py-3 font-semibold rounded-lg shadow-lg transition-all duration-300 transform
                ${buttonState === 'idle' 
                  ? 'bg-blue-600 hover:bg-blue-700 hover:scale-105 text-white' 
                  : buttonState === 'loading' 
                    ? 'bg-blue-500 text-white cursor-not-allowed scale-95' 
                    : 'bg-green-500 text-white scale-105'
                }
                ${buttonState === 'loading' ? 'animate-pulse' : ''}
              `}
              disabled={buttonState === 'loading'}
            >
              {buttonState === 'idle' && (
                <span className="flex items-center">
                  <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                  </svg>
                  Submit Admission Form
                </span>
              )}
              {buttonState === 'loading' && (
                <span className="flex items-center">
                  <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Processing...
                </span>
              )}
              {buttonState === 'success' && (
                <span className="flex items-center">
                  <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                  Success!
                </span>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default AddStudent; 